import { useEffect, useState } from "react";
import { GiCow } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import { api } from "../../services/api";
import { repositorioFazenda } from "../../sync/repositories/fazendas";
import { repositorioAnimal } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Cadastro de Animais",
  descricao:
    "Cadastre aqui cada animal individual da fazenda: sua identificação, espécie, raça e, quando conhecida, a mãe. Esse cadastro é a base para pesagens, lotes, eventos de manejo e movimentações — quanto mais completo, melhor o rastreamento do animal ao longo do tempo.",
  secoes: [
    {
      titulo: "Identificação",
      campos: [
        { nome: "SISBOV", obrigatorio: true, explicacao: "Número de identificação oficial do animal no Sistema Brasileiro de Identificação e Certificação de Bovinos e Bubalinos." },
        { nome: "Número do Brinco", obrigatorio: true, explicacao: "Número do brinco de identificação usado no manejo diário do animal." },
        { nome: "Nome", obrigatorio: false, explicacao: "Nome ou apelido dado ao animal, se houver." },
      ],
    },
    {
      titulo: "Origem e Classificação",
      campos: [
        { nome: "Fazenda", obrigatorio: true, explicacao: "Em qual fazenda o animal está. Se a fazenda ainda não existe, use o botão '+' ao lado do campo para cadastrá-la." },
        { nome: "Espécie", obrigatorio: true, explicacao: "Espécie do animal (ex.: Bovino, Suíno, Caprino). Definir a espécie libera a lista de raças correspondentes." },
        { nome: "Raça", obrigatorio: true, explicacao: "Raça do animal. Só fica disponível depois de escolher a espécie." },
      ],
    },
    {
      titulo: "Dados Gerais",
      campos: [
        { nome: "Sexo", obrigatorio: true, explicacao: "Macho ou Fêmea. Só fêmeas podem depois ser selecionadas como mãe em registros de Natalidade." },
        { nome: "Status", obrigatorio: false, explicacao: "Situação do animal: Ativo (na fazenda), Vendido, Abatido ou Morto. Começa como Ativo." },
        { nome: "Estado Interno", obrigatorio: false, explicacao: "Situação de manejo do animal no momento (Saúde, Reprodução, Confinamento, Embutido)." },
        { nome: "Data de Nascimento", obrigatorio: true, explicacao: "Data de nascimento do animal, usada para calcular idade em outras telas." },
        { nome: "Mãe", obrigatorio: false, explicacao: "Selecione a mãe deste animal, se ela também estiver cadastrada. Ao escolher a mãe, fazenda, espécie e raça são preenchidas automaticamente com os dados dela." },
        { nome: "Peso atual", obrigatorio: false, explicacao: "Peso do animal no momento do cadastro. O histórico de peso é registrado depois na tela de Pesagens." },
      ],
    },
    {
      titulo: "Observações",
      campos: [
        { nome: "Observações", obrigatorio: false, explicacao: "Informações adicionais sobre o animal (histórico de saúde, particularidades, etc.)." },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir um animal exige conexão com a internet — apenas a consulta (listagem e filtros) funciona offline.",
    "Somente fêmeas aparecem na lista de possíveis mães.",
    "A lista de raças só é liberada depois que a espécie é escolhida.",
  ],
};

const GestaoAnimais = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [fazendas, setFazendas] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [racas, setRacas] = useState([]);
  const [maes, setMaes] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [animaisFiltrados, setAnimaisFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  const [todasRacas, setTodasRacas] = useState([]);

  const [filtros, setFiltros] = useState({
    sisbov: "",
    brinco: "",
    nome: "",
    especie: "",
    raca: "",
    fazenda: "",
    categoria: "",
  });
  
  const [formData, setFormData] = useState({
    numero_identificacao: "",
    sisbov: "",
    nome: "",
    especie: "",
    raca: "",
    fazenda: "",
    lote: "",
    sexo: "",
    data_nascimento: "",
    peso: "",
    estado: "S",
    status: "A",
    observacoes: "",
    mae: "",
  });

  useEffect(() => {
    carregarAnimais();
    carregarFazendas();
    carregarEspecies();
    carregarMaes();
  }, []);

  useEffect(() => {
    if (especies.length > 0) {
      carregarTodasRacas();
    }
  }, [especies]);

  const carregarAnimais = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioAnimal.listar();

      const dados = response.data.results || response.data;

      setAnimais(dados);
      setAnimaisFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      alert("Erro ao carregar lista de animais");
    } finally {
      setCarregandoLista(false);
    }
  };

  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  const carregarEspecies = async () => {
    try {
      const response = await api.get("/pecuaria/animal/especies/");
      setEspecies(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao buscar espécies:", error);
    }
  };

  const carregarRacas = async (especieId) => {
    if (!especieId) {
      setRacas([]);
      return;
    }
    
    try {
      const response = await api.get(`/pecuaria/animal/racas/?especie=${especieId}`);
      setRacas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao buscar raças:", error);
      setRacas([]);
    }
  };

  const carregarTodasRacas = async () => {
    try {
      let todas = [];

      for (const especie of especies) {
        const response = await api.get(`/pecuaria/animal/racas/?especie=${especie.id}`);
        const dados = response.data.results || response.data || [];
        todas = [...todas, ...dados];
      }

      setTodasRacas(todas);
    } catch (error) {
      console.error("Erro ao carregar todas raças:", error);
    }
  };

  const carregarMaes = async () => {
    try {
      const response = await repositorioAnimal.listar();

      const dados = response.data.results || response.data;

      const femeas = dados.filter(a => a.sexo === "F" && a.ativo);
      setMaes(femeas);
    } catch (error) {
      console.error("Erro ao buscar mães:", error);
    }
  };

  const getEspecieNome = (id) => {
    const especie = especies.find(e => e.id === id);
    return especie ? especie.nome : '';
  };

  const getRacaNome = (id) => {
    console.log("ID RECEBIDO:", id);
    console.log("TODAS RACAS:", todasRacas);

    const raca = todasRacas.find(r => Number(r.id) === Number(id));
    
    console.log("RACA ENCONTRADA:", raca);

    return raca ? raca.nome : '';
  };

  const getFazendaNome = (id) => {
    const fazenda = fazendas.find(f => f.id === id);
    return fazenda ? fazenda.nome : '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = ["especie", "raca", "fazenda", "lote"].includes(name)
      ? value === "" ? "" : Number(value)
      : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Se for espécie, carregar raças
    if (name === "especie" && value) {
      carregarRacas(parseInt(value));
    }
  };

  // Quando selecionar uma mãe, preencher fazenda, espécie e raça automaticamente
  useEffect(() => {
    if (!formData.mae) return;

    const maeSelecionada = maes.find(m => m.id === formData.mae);

    if (maeSelecionada) {
      setFormData(prev => ({
        ...prev,
        fazenda: maeSelecionada.fazenda,
        especie: maeSelecionada.especie,
        raca: maeSelecionada.raca
      }));
      if (maeSelecionada.especie) {
        carregarRacas(maeSelecionada.especie);
      }
    }
  }, [formData.mae]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dadosParaEnviar = { ...formData };
      
      if (editando) {
        // Editar animal existente
        const response = await api.put(`/pecuaria/animal/${editandoId}/`, dadosParaEnviar);
        if (response.status === 200) {
          alert("Animal atualizado com sucesso!");
          handleCancel();
          carregarAnimais();
        }
      } else {
        // Cadastrar novo animal
        const response = await api.post("/pecuaria/animal/", dadosParaEnviar);
        if (response.status === 200 || response.status === 201) {
          alert("Animal cadastrado com sucesso!");
          handleCancel();
          carregarAnimais();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data?.sisbov) {
        alert(data.sisbov[0]);
      } else if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (animal) => {
    setFormData({
      numero_identificacao: animal.numero_identificacao || "",
      sisbov: animal.sisbov || "",
      nome: animal.nome || "",
      especie: animal.especie?.id || animal.especie || "",
      raca: animal.raca?.id || animal.raca || "",
      fazenda: animal.fazenda?.id || animal.fazenda || "",
      lote: animal.lote || "",
      sexo: animal.sexo || "",
      data_nascimento: animal.data_nascimento || "",
      peso: animal.peso || "",
      estado: animal.estado || "S",
      status: animal.status || "A",
      observacoes: animal.observacoes || "",
      mae: animal.mae || "",
    });
    
    // Carregar raças da espécie do animal
    const especieId = animal.especie?.id || animal.especie;
    if (especieId) {
      carregarRacas(especieId);
    }
    
    setEditando(true);
    setEditandoId(animal.id);
    setMostrarFormulario(true); // Mostrar formulário ao editar
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoAnimal = () => {
    handleCancel(); // Limpa o formulário
    setMostrarFormulario(true); // Mostra o formulário
  };

  const handleCancel = () => {
    setFormData({
      numero_identificacao: "",
      sisbov: "",
      nome: "",
      especie: "",
      raca: "",
      fazenda: "",
      lote: "",
      sexo: "",
      data_nascimento: "",
      peso: "",
      estado: "S",
      status: "A",
      observacoes: "",
      mae: "",
    });
    setEditando(false);
    setEditandoId(null);
    setRacas([]);
    setMostrarFormulario(false); // Fecha o formulário
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este animal? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/animal/${id}/`);
      alert("Animal excluído com sucesso!");
      carregarAnimais();
      
      // Se estava editando este animal, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir animal");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...animais];
    
    if (filtros.sisbov) {
      filtrados = filtrados.filter(a => 
        a.sisbov?.toLowerCase().includes(filtros.sisbov.toLowerCase())
      );
    }
    
    if (filtros.brinco) {
      filtrados = filtrados.filter(a => 
        a.numero_identificacao?.toLowerCase().includes(filtros.brinco.toLowerCase())
      );
    }
    
    if (filtros.nome) {
      filtrados = filtrados.filter(a => 
        a.nome?.toLowerCase().includes(filtros.nome.toLowerCase())
      );
    }
    
    if (filtros.especie) {
      filtrados = filtrados.filter(a => a.especie === parseInt(filtros.especie));
    }
    
    if (filtros.raca) {
      filtrados = filtrados.filter(a => a.raca === parseInt(filtros.raca));
    }
    
    if (filtros.fazenda) {
      filtrados = filtrados.filter(a => a.fazenda === parseInt(filtros.fazenda));
    }

    if (filtros.categoria) {
      filtrados = filtrados.filter(a => a.categoria === filtros.categoria);
    }

    setAnimaisFiltrados(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      sisbov: "",
      brinco: "",
      nome: "",
      especie: "",
      raca: "",
      fazenda: "",
      categoria: "",
    });
    setAnimaisFiltrados(animais);
  };

  // Formatar status
  const getStatusLabel = (status) => {
    const statusMap = {
      'A': 'Ativo',
      'V': 'Vendido',
      'B': 'Abatido',
      'M': 'Morto'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'A': 'bg-green-100 text-green-800',
      'V': 'bg-yellow-100 text-yellow-800',
      'B': 'bg-red-100 text-red-800',
      'M': 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getSexoLabel = (sexo) => {
    return sexo === 'M' ? 'Macho' : 'Fêmea';
  };

  const CATEGORIA_LABEL = {
    bezerro: 'Bezerro',
    novilhos: 'Novilho',
    novilho_jovem: 'Novilho Jovem',
    vaca: 'Vaca',
    boi_adulto: 'Boi Adulto',
  };

  const CATEGORIA_COR = {
    bezerro: 'bg-yellow-100 text-yellow-800',
    novilhos: 'bg-blue-100 text-blue-800',
    novilho_jovem: 'bg-indigo-100 text-indigo-800',
    vaca: 'bg-pink-100 text-pink-800',
    boi_adulto: 'bg-orange-100 text-orange-800',
  };

  const calcularCategoriaLocal = (especie, dataNascimento, sexo) => {
    if (Number(especie) !== 1 || !dataNascimento || !sexo) return null;
    const hoje = new Date();
    const nasc = new Date(dataNascimento + 'T00:00:00');
    let meses = (hoje.getFullYear() - nasc.getFullYear()) * 12 + (hoje.getMonth() - nasc.getMonth());
    if (hoje.getDate() < nasc.getDate()) meses--;
    if (meses < 0) return null;
    if (meses < 9) return 'bezerro';
    if (meses < 24) return 'novilhos';
    if (meses < 36) return 'novilho_jovem';
    return sexo === 'F' ? 'vaca' : 'boi_adulto';
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center  mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Animais <GiCow size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoAnimal}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Animal
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição - Aparece apenas quando necessário */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Animal" : "Cadastrar Novo Animal"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">SISBOV *</label>
                    <input
                      type="text"
                      name="sisbov"
                      value={formData.sisbov}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Número do Brinco *</label>
                    <input
                      type="text"
                      name="numero_identificacao"
                      value={formData.numero_identificacao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Espécie *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="especie"
                        value={formData.especie}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione</option>
                        {especies.map(e => (
                          <option key={e.id} value={e.id}>{e.nome}</option>
                        ))}
                      </select>
                      {formData.especie && (
                        <img
                          src={`/icons/${getEspecieNome(formData.especie).toLowerCase()}.png`}
                          alt={getEspecieNome(formData.especie)}
                          className="w-10 h-10 object-contain"
                          onError={(e) => {
                            e.target.src = "/icons/animal.png";
                          }}
                        />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Raça *</label>
                    <select
                      name="raca"
                      value={formData.raca}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      disabled={!formData.especie || racas.length === 0}
                      required
                    >
                      <option value="">Selecione</option>
                      {racas.map(r => (
                        <option key={r.id} value={r.id}>{r.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Sexo *</label>
                    <select
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione</option>
                      <option value="M">Macho</option>
                      <option value="F">Fêmea</option>
                    </select>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    >
                      <option value="A">Ativo (Na Fazenda)</option>
                      <option value="V">Vendido</option>
                      <option value="B">Abatido</option>
                      <option value="M">Morto</option>
                    </select>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Estado Interno</label>
                    <select
                      name="estado"
                      value={formData.estado}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    >
                      <option value="S">Saúde</option>
                      <option value="R">Reprodução</option>
                      <option value="C">Confinamento</option>
                      <option value="E">Embutido</option>
                    </select>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                    {(() => {
                      const cat = calcularCategoriaLocal(formData.especie, formData.data_nascimento, formData.sexo);
                      return cat ? (
                        <span className={`mt-1 self-start px-2 py-0.5 rounded-full text-xs font-semibold ${CATEGORIA_COR[cat]}`}>
                          {CATEGORIA_LABEL[cat]}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Mãe (opcional)</label>
                    <select
                      name="mae"
                      value={formData.mae}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    >
                      <option value="">Sem mãe</option>
                      {maes.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.numero_identificacao} - {m.nome || "Sem nome"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Peso atual</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="peso"
                        value={formData.peso}
                        onChange={handleChange}
                        className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0"
                      />
                      <div className="h-6 w-px bg-green-500" />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    rows="3"
                    placeholder="Digite observações..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-green-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        {!mostrarFormulario && (
          <>
          {/* Seção de Filtros - Sempre visível */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros de Busca
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">SISBOV</label>
                <input
                  type="text"
                  placeholder="Buscar por SISBOV..."
                  value={filtros.sisbov}
                  onChange={(e) => setFiltros({...filtros, sisbov: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Número do Brinco</label>
                <input
                  type="text"
                  placeholder="Buscar por brinco..."
                  value={filtros.brinco}
                  onChange={(e) => setFiltros({...filtros, brinco: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Nome do Animal</label>
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={filtros.nome}
                  onChange={(e) => setFiltros({...filtros, nome: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Espécie</label>
                <select
                  value={filtros.especie}
                  onChange={(e) => {
                    setFiltros({...filtros, especie: e.target.value, raca: ""});
                    if (e.target.value) {
                      carregarRacas(parseInt(e.target.value));
                    }
                  }}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                >
                  <option value="">Todas as espécies</option>
                  {especies.map(e => (
                    <option key={e.id} value={e.id}>{e.nome}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Raça</label>
                <select
                  value={filtros.raca}
                  onChange={(e) => setFiltros({...filtros, raca: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  disabled={!filtros.especie}
                >
                  <option value="">Todas as raças</option>
                  {racas.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Fazenda</label>
                <select
                  value={filtros.fazenda}
                  onChange={(e) => setFiltros({...filtros, fazenda: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                >
                  <option value="">Todas as fazendas</option>
                  {fazendas.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
              </div>

              {filtros.especie === "1" && (
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={filtros.categoria}
                    onChange={(e) => setFiltros({...filtros, categoria: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todas as categorias</option>
                    <option value="bezerro">Bezerro</option>
                    <option value="novilhos">Novilho</option>
                    <option value="novilho_jovem">Novilho Jovem</option>
                    <option value="vaca">Vaca</option>
                    <option value="boi_adulto">Boi Adulto</option>
                  </select>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={aplicarFiltros}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>

          {/* Tabela de Animais - Sempre visível */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">
                {animaisFiltrados.length} animal(is) encontrado(s)
              </p>
            </div>
            
            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">SISBOV</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Brinco</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Espécie</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Raça</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Sexo</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Categoria</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {animaisFiltrados.map((animal) => (
                      <tr key={animal.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-sm">{animal.sisbov || "—"} </td>
                        <td className="p-3 text-sm font-mono">{animal.numero_identificacao}</td>
                        <td className="p-3 text-sm font-medium">{animal.nome || "—"}</td>
                        <td className="p-3 text-sm">{getEspecieNome(animal.especie?.id || animal.especie)}</td>
                        <td className="p-3 text-sm">{getRacaNome(animal.raca?.id || animal.raca)}</td>
                        <td className="p-3 text-sm">{getFazendaNome(animal.fazenda?.id || animal.fazenda)}</td>
                        <td className="p-3 text-sm">{getSexoLabel(animal.sexo)}</td>
                        <td className="p-3">
                          {animal.categoria ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${CATEGORIA_COR[animal.categoria] || 'bg-gray-100 text-gray-700'}`}>
                              {CATEGORIA_LABEL[animal.categoria] || animal.categoria}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(animal.status)}`}>
                            {getStatusLabel(animal.status)}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditar(animal)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Editar"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleExcluir(animal.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Excluir"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {animaisFiltrados.length === 0 && (
                      <tr>
                        <td colSpan="10" className="p-8 text-center text-gray-500">
                          Nenhum animal encontrado com os filtros selecionados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
        )}

      </div>
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
          setMostrarModalFazenda(false);
        }}
      />
    </Layout>
  );
};

export default GestaoAnimais;